import { useState } from "preact/hooks"
import useSWR from "swr"
import { Link } from "wouter-preact"
import { ChevronDown, ChevronRight, Loader2, Mail } from "lucide-react"

import {
  BrandResponse,
  DeliveriesResponse,
  DeliveryLine,
  DeliveryOrder,
  fetchDeliveries,
  updateInboundPoLine,
} from "@/api/inbound_po_line.tsx"
import { UserState } from "@/auth/user"
import { ShortLineAnswer } from "@/components/ShortLineAnswer.tsx"
import { StatCard } from "@/components/StatCard.tsx"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { toast } from "@/components/ui/use-toast"
import {
  DeliveryFilter,
  StatusTone,
  delivery_status,
  filter_orders,
  format_day,
  format_units,
  shortage_dispute_mailto,
} from "@/deliveries/deliveries_display.ts"
import { format_rate } from "@/fill_rate/fill_rate_display.ts"
import { exactCurrencyFormatter, usePageTitle } from "@/utils"

const DAYS = 180

const FILTERS: { key: DeliveryFilter; label: string }[] = [
  { key: "attention", label: "Needs attention" },
  { key: "late", label: "Late" },
  { key: "short", label: "Short" },
  { key: "open", label: "Being received" },
  { key: "all", label: "All" },
]

const TONE_CLASS: Record<StatusTone, string> = {
  open: "bg-slate-100 text-slate-700",
  ok: "bg-emerald-100 text-emerald-800",
  late: "bg-amber-100 text-amber-800",
  short: "bg-rose-100 text-rose-700",
  late_short: "bg-rose-100 text-rose-700",
}

function Summary({ data, className }: { data: DeliveriesResponse; className?: string }) {
  const s = data.summary
  const closed = s.on_time + s.late
  const money = (amount: number) => exactCurrencyFormatter(amount, data.currency)
  return (
    <div class={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${className ?? ""}`}>
      <StatCard title="On time, in full" value={format_rate(s.otif_rate)} detail={`${closed} delivered PO${closed === 1 ? "" : "s"} with a target date`} />
      <StatCard
        title="On time"
        value={format_rate(s.on_time_rate)}
        detail={s.late ? `${s.late} late · up to ${money(s.late_damages_cap)} in late-delivery damages` : "None late"}
        warn={s.late > 0}
      />
      <StatCard
        title="In full"
        value={format_rate(s.in_full_rate)}
        detail={s.short ? `${s.short} short · ${money(s.shorted_value)} + VAT at H&B's cost` : "None short"}
        warn={s.short > 0}
      />
      <StatCard title="Being received" value={String(s.open)} detail="Open POs H&B has not closed" />
    </div>
  )
}

function PaidBy({ order, currency }: { order: DeliveryOrder; currency: string }) {
  if (!order.invoice) {
    return <span class="text-slate-400">—</span>
  }
  const invoice = order.invoice
  return (
    <div>
      <Link href={`/deduction/${invoice.deduction_id}`} class="text-blue-700 hover:underline font-mono">
        {invoice.invoice_number}
      </Link>
      <div class="text-slate-500">
        {exactCurrencyFormatter(invoice.amount, currency)}
        {invoice.check_date && <> · {format_day(invoice.check_date)}</>}
      </div>
      {invoice.paid_for_received_only && <Badge variant="rose">Paid for received only</Badge>}
    </div>
  )
}

function LinesTable({ order, currency, supplier_number, onAnswered }: { order: DeliveryOrder; currency: string; supplier_number: string | null; onAnswered: () => void }) {
  const signer = UserState.use((u) => [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.email || "")
  const [saving_id, setSavingId] = useState<string | null>(null)

  async function answer(line: DeliveryLine, response: BrandResponse | null) {
    setSavingId(line.id)
    const res = await updateInboundPoLine(line.id, response)
    setSavingId(null)
    if (!res.ok) {
      toast({ title: "Could not save the answer", description: res.error.message, variant: "destructive" })
      return
    }
    onAnswered()
    if (response === "shipped_in_full") {
      window.location.href = shortage_dispute_mailto(order, line, signer, supplier_number)
    }
  }

  return (
    <table class="w-full text-sm">
      <thead>
        <tr class="text-left text-slate-500">
          <th class="py-1 pr-4 font-medium">SKU</th>
          <th class="py-1 pr-4 font-medium text-right">Ordered</th>
          <th class="py-1 pr-4 font-medium text-right">Received</th>
          <th class="py-1 pr-4 font-medium text-right">Unit cost</th>
          <th class="py-1 pr-4 font-medium">Last received</th>
          <th class="py-1 font-medium">Your answer</th>
        </tr>
      </thead>
      <tbody>
        {order.lines.map((line: DeliveryLine) => (
          <tr key={line.id} class="align-top">
            <td class="py-1 pr-4">
              <span class="font-mono">{line.item_number}</span>
              {line.description && <span class="text-slate-500"> · {line.description}</span>}
            </td>
            <td class="py-1 pr-4 text-right">{format_units(line.quantity_ordered)}</td>
            <td class={`py-1 pr-4 text-right ${line.short_units > 0 ? "text-rose-700 font-medium" : ""}`}>
              {line.quantity_received === null ? "receiving" : format_units(line.quantity_received)}
            </td>
            <td class="py-1 pr-4 text-right">{line.unit_cost === null ? "—" : exactCurrencyFormatter(line.unit_cost, currency)}</td>
            <td class="py-1 pr-4 whitespace-nowrap">{format_day(line.received_date)}</td>
            <td class="py-1">
              {line.short_units > 0 ? (
                <>
                  <ShortLineAnswer brand_response={line.brand_response} saving={saving_id === line.id} onAnswer={(r: BrandResponse | null) => answer(line, r)} />
                  {line.brand_response === "shipped_in_full" && (
                    <a href={shortage_dispute_mailto(order, line, signer, supplier_number)} class="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                      <Mail className="h-3 w-3" /> Email H&B Accounts Payable
                    </a>
                  )}
                </>
              ) : (
                <span class="text-slate-400">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function OrderRow({ order, data, onAnswered }: { order: DeliveryOrder; data: DeliveriesResponse; onAnswered: () => void }) {
  const [open, setOpen] = useState(false)
  const status = delivery_status(order)
  const money = (amount: number) => exactCurrencyFormatter(amount, data.currency)
  return (
    <>
      <tr class="border-b align-top cursor-pointer hover:bg-slate-50" onClick={() => setOpen(!open)}>
        <td class="py-2 pr-2 text-slate-400">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
        <td class="py-2 pr-4 font-mono">{order.po_number}</td>
        <td class="py-2 pr-4 whitespace-nowrap">{format_day(order.expected_date)}</td>
        <td class="py-2 pr-4 whitespace-nowrap">{format_day(order.last_received)}</td>
        <td class="py-2 pr-4">
          <span class={`inline-block rounded px-2 py-0.5 text-xs font-medium ${TONE_CLASS[status.tone]}`}>{status.label}</span>
        </td>
        <td class="py-2 pr-4 text-right">{format_units(order.ordered_units)}</td>
        <td class="py-2 pr-4 text-right">{order.ordered_value === null ? "—" : money(order.ordered_value)}</td>
        <td class={`py-2 pr-4 text-right ${order.shorted_value > 0 ? "text-rose-700" : ""}`}>{order.shorted_value > 0 ? money(order.shorted_value) : "—"}</td>
        <td class="py-2 pr-4 text-right">{order.late_damages_cap === null ? "—" : money(order.late_damages_cap)}</td>
        <td class="py-2" onClick={(e: MouseEvent) => e.stopPropagation()}>
          <PaidBy order={order} currency={data.currency} />
        </td>
      </tr>
      {open && (
        <tr class="border-b bg-slate-50">
          <td />
          <td colSpan={9} class="py-2 pr-4">
            <LinesTable order={order} currency={data.currency} supplier_number={data.supplier_number} onAnswered={onAnswered} />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * Holland & Barrett deliveries: each PO as H&B's Vendor Portal recorded it — received when, against
 * the Target Delivery Date, and in what quantity — with what a late or short delivery exposes the
 * brand to under H&B's Standard Conditions, and the payment that settled it.
 */
export function DeliveriesPage() {
  usePageTitle("H&B deliveries")
  const [filter, setFilter] = useState<DeliveryFilter>("attention")
  const { data, error, isLoading, mutate } = useSWR<DeliveriesResponse>(["hb-deliveries", DAYS], () => fetchDeliveries(DAYS), {
    revalidateOnFocus: false,
  })

  if (isLoading) {
    return (
      <div class="flex items-center gap-2 p-8 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading deliveries…
      </div>
    )
  }
  if (error || !data) {
    return <div class="p-8 text-red-600">Could not load deliveries.</div>
  }

  const visible = filter_orders(data.orders, filter)
  return (
    <div class="p-6 flex flex-col gap-6 max-w-7xl">
      <div>
        <h1 class="text-2xl font-semibold">Holland & Barrett deliveries</h1>
        <p class="text-slate-600 mt-1 max-w-3xl">
          Every PO in H&B's Vendor Portal over the last {DAYS} days: what H&B received and when, against the target delivery date.
          H&B can charge 3% of the price for each week a delivery is late (12% at most), and must tell you about a shortage in
          writing within 72 hours of delivery. If you delivered a line in full that H&B counts short, say so here.
        </p>
      </div>

      {data.orders.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-slate-500">No Holland & Barrett deliveries yet. They appear after the weekday Vendor Portal sync.</CardContent>
        </Card>
      ) : (
        <>
          <Summary data={data} />
          <Card>
            <CardHeader>
              <div class="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="text-base">Purchase orders</CardTitle>
                  <CardDescription>Click a PO for its lines.</CardDescription>
                </div>
                <div class="flex gap-1 flex-wrap">
                  {FILTERS.map((f) => (
                    <Button key={f.key} size="sm" variant={filter === f.key ? "default" : "ghost"} onClick={() => setFilter(f.key)}>
                      {f.label} ({filter_orders(data.orders, f.key).length})
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {visible.length === 0 ? (
                <div class="text-sm text-slate-500 py-6 text-center">Nothing here.</div>
              ) : (
                <div class="overflow-x-auto">
                  <table class="w-full min-w-[1000px] text-sm">
                    <thead>
                      <tr class="text-left text-slate-500 border-b">
                        <th class="py-2 pr-2" />
                        <th class="py-2 pr-4 font-medium">PO</th>
                        <th class="py-2 pr-4 font-medium">Target</th>
                        <th class="py-2 pr-4 font-medium">Received</th>
                        <th class="py-2 pr-4 font-medium">Delivery</th>
                        <th class="py-2 pr-4 font-medium text-right">Units</th>
                        <th class="py-2 pr-4 font-medium text-right">Value</th>
                        <th class="py-2 pr-4 font-medium text-right">Short value</th>
                        <th class="py-2 pr-4 font-medium text-right">Max late charge</th>
                        <th class="py-2 font-medium">Paid by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((order: DeliveryOrder) => (
                        <OrderRow key={order.po_number} order={order} data={data} onAnswered={() => mutate()} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
